/**
 * PATH: packages/ui/src/components/settings/ModuleAccessTab.tsx
 * PURPOSE: Toggle organisation-level module access flags
 *
 * WHY: Admins need to enable or disable hubs per organisation without
 *      leaving the settings modal.
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌──────────────┐
 *   │ Load Flags  │───▶│ Toggle Hub   │───▶│ Save Updates │
 *   └─────────────┘    └──────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - SettingsApiClient: API contract for persistence
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, CheckCircle2, Loader2, Users } from 'lucide-react'
import { cn } from '../../lib/utils'
import { subfeaturesForModuleFlag } from '../../access/hubSubfeatures'
import type { SettingsApiClient } from './types'

interface ModuleAccessFlags {
  enable_quote_builder?: boolean
  enable_cfo_module?: boolean
  enable_quality_assurance?: boolean
  enable_workforce_hub?: boolean
  enable_billing_automation?: boolean
  enable_ai_hub?: boolean
  enable_qdrive?: boolean
  enable_emails?: boolean
}

export interface ModuleAccessTabProps {
  apiClient?: SettingsApiClient
  organisationId?: string
  endpoint?: string
  className?: string
}

const MODULES: Array<{
  key: keyof ModuleAccessFlags
  label: string
  description: string
}> = [
  { key: 'enable_quote_builder', label: 'Quote Builder', description: 'Enable Quote Builder and Tracker.' },
  { key: 'enable_cfo_module', label: 'CFO Hub', description: 'Financial dashboards and Xero data.' },
  { key: 'enable_quality_assurance', label: 'QA Hub', description: 'Inspections, NCRs, and QA workflows.' },
  { key: 'enable_workforce_hub', label: 'Workforce Hub', description: 'Timesheets and resource planning.' },
  { key: 'enable_billing_automation', label: 'Billing Hub', description: 'Billing automation and subscriptions.' },
  { key: 'enable_ai_hub', label: 'AI Hub', description: 'AI copilots and automation tools.' },
  { key: 'enable_qdrive', label: 'QDrive', description: 'Document storage and collaboration.' },
  { key: 'enable_emails', label: 'Emails', description: 'Email sync and shared inboxes.' },
]

export function ModuleAccessTab({
  apiClient,
  organisationId,
  // NOTE: apiClient baseURL already includes /api, so endpoint should NOT include /api prefix
  endpoint = '/settings/access-control/',
  className,
}: ModuleAccessTabProps) {
  const [flags, setFlags] = useState<ModuleAccessFlags>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const savedFlagsRef = useRef<ModuleAccessFlags>({})

  const canUseApi = useMemo(() => !!apiClient && !!organisationId, [apiClient, organisationId])

  const fetchFlags = useCallback(async () => {
    if (!apiClient || !organisationId) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(endpoint, { params: { tenant: organisationId } })
      const data = response?.data ?? response
      const loaded = data?.module_access || {}
      setFlags(loaded)
      savedFlagsRef.current = { ...loaded }
      setIsDirty(false)
    } catch (err) {
      if (import.meta.env.DEV) console.error('[ModuleAccess] Failed to fetch:', err)
      setError('Failed to load module access settings')
    } finally {
      setIsLoading(false)
    }
  }, [apiClient, endpoint, organisationId])

  useEffect(() => {
    fetchFlags()
  }, [fetchFlags])

  const handleToggle = (key: keyof ModuleAccessFlags) => {
    setFlags(prev => {
      const next = { ...prev, [key]: !prev[key] }
      // Check if flags differ from last saved state
      const changed = Object.keys({ ...next, ...savedFlagsRef.current }).some(
        k => next[k as keyof ModuleAccessFlags] !== savedFlagsRef.current[k as keyof ModuleAccessFlags]
      )
      setIsDirty(changed)
      return next
    })
  }

  const handleSave = async () => {
    if (!apiClient || !organisationId) return
    setIsSaving(true)
    setError(null)
    try {
      await apiClient.put(endpoint, { tenant: organisationId, module_access: flags })
      savedFlagsRef.current = { ...flags }
      setIsDirty(false)
    } catch (err) {
      if (import.meta.env.DEV) console.error('[ModuleAccess] Failed to save:', err)
      setError('Failed to save module access settings')
    } finally {
      setIsSaving(false)
    }
  }

  if (!canUseApi) {
    return (
      <div className={cn('p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40', className)}>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Module access settings are not available in this hub.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-4 max-w-2xl', className)}>
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Module Access</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Enable or disable hubs for this organisation.
        </p>
      </div>

      {/* Cross-reference note: admins conflate these company-wide switches with
          the per-member access matrix. Spell out the two axes so "why don't
          these match the matrix?" stops being a question. Settings = whole
          company on/off; People -> Access & roles = per-person view/edit/none. */}
      <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-600 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-300">
        <Users className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
        <p>
          These switches turn whole modules on or off for the{' '}
          <span className="font-medium">entire company</span>. To control what each
          individual member can do within a hub (view / edit / no access), go to{' '}
          <span className="font-medium">People → Access &amp; roles</span>.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading module access...
        </div>
      )}

      <div className="space-y-3">
        {MODULES.map((module) => {
          const enabled = !!flags[module.key]
          // Sub-features that live UNDER this module (SSOT: @quotech/ui/access).
          // Shown so an admin sees that e.g. turning off Workforce also hides
          // Timesheets / Schedule / Diary — the module<->matrix relationship.
          const includes = subfeaturesForModuleFlag(module.key as string)
          return (
            <div key={module.key} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800">
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{module.label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{module.description}</p>
                {includes.length > 0 && (
                  <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
                    Includes: {includes.map((s) => s.label).join(', ')}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleToggle(module.key)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-lg border',
                  enabled
                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800'
                    : 'bg-slate-50 text-slate-500 border-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700'
                )}
              >
                {enabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
          )
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={isSaving || !isDirty}
          className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
        </button>
        {!isSaving && !isDirty && !error && (
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4" /> Synced
          </span>
        )}
        {isDirty && !isSaving && (
          <span className="text-xs text-amber-500 flex items-center gap-1">
            Unsaved changes
          </span>
        )}
      </div>
    </div>
  )
}

export default ModuleAccessTab
